
import { computed, nextTick, ref, watch } from 'vue';
import { useStoreInNodes } from '@/composables/useStoreInNodes';
import { onClickOutside } from '@/directives/onClickOutside';
import { GetAmpStorage, GetAvailableAccountKeys, GetStorageEntries } from '@/constants/getters';

export default {
  directives: {
    'click-outside': onClickOutside,
  },

  props: {
    storageKey: { type: String, default: null },
    storageType: { type: String, default: undefined },
    maxSize: { type: Number, default: null },
    readonly: { type: Boolean, default: false },
  },

  setup(props, { emit }) {
    const { store } = useStoreInNodes();
    const selectValue = ref('');
    const toggled = ref(false);
    const selectInput = ref(null);

    const crmAccountKeys = computed(() => store.value.getters[`crmStore/${GetAvailableAccountKeys}`]);
    const storage = computed(() => store.value.getters[`ampStore/${GetAmpStorage}`]);
    const storageEntries = computed(() => store.value.getters[`ampStore/${GetStorageEntries}`]);
    const storageValue = computed(() => getKeyValue(props.storageKey));
    const hasStorageValue = computed(() => !toggled.value && storageValue.value && storageValue.value !== '');

    const filteredStorageValues = computed(() =>
      !storage.value
        ? []
        : Object.entries(storage.value).filter(([_key, entry]) =>
            entry?.toLowerCase().includes(selectValue.value.toLowerCase())
          )
    );

    const filteredCrmKeys = computed(() =>
      selectValue.value
        ? crmAccountKeys.value.filter((key) => (key.name || '').toLowerCase().includes(selectValue.value.toLowerCase()))
        : crmAccountKeys.value
    );

    const buttonStyle = computed(() => ({
      'toggled-btn': toggled.value,
      'active-btn': hasStorageValue.value,
    }));
    const spannedButtonStyle = computed(() => (hasStorageValue.value ? [{ maxWidth: `${props.maxSize - 2}px` }] : []));
    const dataStorageStyle = computed(() => ({
      'auto-width': hasStorageValue.value,
      'full-width': toggled.value,
      readonly: props.readonly,
    }));

    const getKeyValue = (key) => {
      if (props.storageType === 'crm') {
        return crmAccountKeys.value.find((accountKey) => accountKey.name === key)?.name || '';
      }

      // storageType is internal or undefined
      // we also need to check for the CRM keys to ensure that the old version without storageType works properly
      return storage.value?.[key] || crmAccountKeys.value.find((accountKey) => accountKey.name === key)?.name || '';
    };

    watch(
      () => props.storageKey,
      (newKey) => {
        if (newKey) {
          selectValue.value = getKeyValue(newKey);
        }
      }
    );

    const onOpenStorage = async () => {
      if (props.readonly) {
        return;
      }

      emit('open-storage');
      toggled.value = true;

      await nextTick();
      focusInput();
    };

    const focusInput = () => selectInput.value?.focus();

    const onClickOutsideOfStorage = () => (toggled.value = false);

    const onSelectOption = (key, type) => {
      emit('add-storage-option', { storage: key, storageType: type });
      selectValue.value = type === 'crm' ? key : storage.value[key];
      toggled.value = false;
    };

    const onDeleteStorage = () => {
      emit('delete-storage');
      toggled.value = false;
      selectValue.value = '';
    };

    return {
      buttonStyle,
      dataStorageStyle,
      filteredCrmKeys,
      filteredStorageValues,
      hasStorageValue,
      toggled,
      selectInput,
      selectValue,
      spannedButtonStyle,
      storageEntries,
      storageValue,
      focusInput,
      onClickOutsideOfStorage,
      onDeleteStorage,
      onOpenStorage,
      onSelectOption,
    };
  },
};
